import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { LoggedInHomepage } from "@/components/dashboard/LoggedInHomepage";
import { OnboardingManager } from "@/components/onboarding/OnboardingManager";
import HeroSection from "@/components/HeroSection";
import FeaturesSection from "@/components/FeaturesSection";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { MinimalPathChoice } from "@/components/sections/MinimalSections";
import { EnhancedSearchForm } from "@/components/search/EnhancedSearchForm";
import { useDropOffTracking } from "@/hooks/useDropOffTracking";

const Index = () => {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const { trackLandingPageView } = useDropOffTracking();
  const trackedRef = useRef(false);
  const navigate = useNavigate();

  // Check for auth tokens in URL hash (password recovery, email confirmation, etc.)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const tokenType = hashParams.get("type");
      const accessToken = hashParams.get("access_token");
      
      // Redirect to password reset page if this is a recovery token
      if (tokenType === "recovery" && accessToken) {
        navigate(`/password-reset${hash}`, { replace: true });
        return;
      }
      
      // Redirect to email confirmed page if this is a signup confirmation
      if (tokenType === "signup" && accessToken) {
        navigate(`/email-confirmed${hash}`, { replace: true });
        return;
      }
    }
  }, [navigate]);

  // Track landing page view for host acquisition funnel
  useEffect(() => {
    if (!loading && !user && !trackedRef.current) {
      trackedRef.current = true;
      trackLandingPageView();
    }
  }, [loading, user, trackLandingPageView]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <LoggedInHomepage />;
  }

  return (
    <div className="min-h-screen bg-background">
      <OnboardingManager />
      <Header />
      <div className="bg-amber-50 dark:bg-amber-950/20 border-b border-amber-200 dark:border-amber-800">
        <div className="container mx-auto px-4 py-3 text-center">
          <p 
            className="text-sm text-amber-800 dark:text-amber-200"
            dangerouslySetInnerHTML={{ __html: t('banner.warning') }}
          />
        </div>
      </div>
      <main>
        {/* Direkter Sucheinstieg: Ort, Zeitraum, Gäste – dann sofort Ergebnisse */}
        <section className="bg-muted/20 border-b border-border">
          <div className="container mx-auto px-4 py-10">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-6">
                <h2 className="text-2xl md:text-3xl font-bold mb-2">
                  {t('home.searchTitle')}
                </h2>
                <p className="text-muted-foreground">
                  {t('home.searchSubtitle')}
                </p>
              </div>
              <EnhancedSearchForm />
            </div>
          </div>
        </section>
        <HeroSection />
        <FeaturesSection />
        <MinimalPathChoice />
        {/* Soft CTA: encourage browsing without forcing signup */}
        <section className="bg-muted/30 border-y border-border">
          <div className="container mx-auto px-4 py-8 text-center">
            <p className="text-base text-muted-foreground mb-3">
              {t('home.browseNote')}
            </p>
            <a
              href="/listings"
              className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
            >
              {t('home.browseAll')} →
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
